/** @format */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  CriteoTrackTransactionResponse,
  BeaconRequestBody,
  CriteoViewItemResponse,
  Environment,
  OcpRequestBody,
  PdpRequestBody,
  SrlpRequestBody,
  CriteoViewProductsResponse,
  PlpRequestBody,
} from '@shared/data-access/interfaces';
import { Logger } from '@shared/data-access/services';
import { catchError, EMPTY, Observable, of } from 'rxjs';
import {
  EMPTY_CRITEO_TRACK_TRANSACTION_RESPONSE,
  EMPTY_CRITEO_VIEW_ITEM_RESPONSE,
  EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE,
} from './criteo.constants';
import { CustomHttpHeaders } from '@shared/data-access/enums';
import { DCSG_CONSTANTS } from '@shared/data-access/constants';

@Injectable({
  providedIn: 'root',
})
export class DmnAdServerDao {
  private readonly http = inject(HttpClient);
  private readonly environment = inject(Environment);
  private readonly logger = inject(Logger);

  getPdpAds(requestBody: PdpRequestBody): Observable<CriteoViewItemResponse> {
    const url = `${this.environment.api.dmnAdServer}/pdp`;
    const httpOptions = {
      headers: new HttpHeaders().set(
        CustomHttpHeaders.X_TIMEOUT,
        `${DCSG_CONSTANTS.timeouts.xshort}`,
      ),
    };
    return this.http
      .post<CriteoViewItemResponse>(url, requestBody, httpOptions)
      .pipe(catchError(this.handleError(EMPTY_CRITEO_VIEW_ITEM_RESPONSE)));
  }

  getPlpAds(requestBody: PlpRequestBody): Observable<CriteoViewProductsResponse> {
    const url = `${this.environment.api.dmnAdServer}/plp`;
    const httpOptions = {
      headers: new HttpHeaders().set(
        CustomHttpHeaders.X_TIMEOUT,
        `${DCSG_CONSTANTS.timeouts.xxshort}`,
      ),
    };
    return this.http
      .post<CriteoViewProductsResponse>(url, requestBody, httpOptions)
      .pipe(catchError(this.handleError(EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE)));
  }

  getSrlpAds(requestBody: SrlpRequestBody): Observable<CriteoViewProductsResponse> {
    const url = `${this.environment.api.dmnAdServer}/srlp`;
    const httpOptions = {
      headers: new HttpHeaders().set(
        CustomHttpHeaders.X_TIMEOUT,
        `${DCSG_CONSTANTS.timeouts.xxshort}`,
      ),
    };
    return this.http
      .post<CriteoViewProductsResponse>(url, requestBody, httpOptions)
      .pipe(catchError(this.handleError(EMPTY_CRITEO_VIEW_PRODUCTS_RESPONSE)));
  }

  getOcpAds(requestBody: OcpRequestBody): Observable<CriteoTrackTransactionResponse> {
    const url = `${this.environment.api.dmnAdServer}/ocp`;
    const httpOptions = {
      headers: new HttpHeaders().set(
        CustomHttpHeaders.X_TIMEOUT,
        `${DCSG_CONSTANTS.timeouts.xshort}`,
      ),
    };
    return this.http
      .post<CriteoTrackTransactionResponse>(url, requestBody, httpOptions)
      .pipe(catchError(this.handleError(EMPTY_CRITEO_TRACK_TRANSACTION_RESPONSE)));
  }

  sendBeacon(
    beaconRequestBody: BeaconRequestBody,
    beaconQueryParameters: string,
  ): Observable<void> {
    const url = `${this.environment.api.dmnAdServer}/send-beacon?${beaconQueryParameters}`;
    const httpOptions = {
      headers: new HttpHeaders().set(
        CustomHttpHeaders.X_TIMEOUT,
        `${DCSG_CONSTANTS.timeouts.xshort}`,
      ),
      keepalive: true,
    };
    return this.http.put<void>(url, beaconRequestBody, httpOptions).pipe(
      catchError((error) => {
        this.logger.error('Error sending beacon from DMN Ad Server', error);
        return EMPTY;
      }),
    );
  }

  private handleError<T extends object>(fallbackValue: T) {
    return (error: unknown): Observable<T> => {
      this.logger.error('Error fetching ads from DMN Ad Server', error);
      return of({ ...fallbackValue });
    };
  }
}
